/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { Fragment, useCallback } from 'react'
import { ListView } from 'antd-mobile'
import { observer } from 'mobx-react'
import { isMobile } from 'react-device-detect'
import _ from 'lodash'
import useRootStore from '@store/useRootStore'
import { useOnMount, useOnUnmount } from '@utils/reactExt'

// css
import { AdvCommentListItem } from '@components/AdvComment/AdvCommentListItem'
import { Schema$UserComment, Schema$UserCommentExt } from '@interface/comment'
import { AdvComments } from '@components/AdvComment/index.style'
import { COMMUNICATION_BLOCK_TYPE, FROM_VIEW_TYPE, USER_COMMENT_TYPE } from '@v2/constants/common'
import { AdvEmpty } from '@components/AdvEmpty'
import { EMPTY_ICON } from '@constants/index'
import { AdvCommentEditor } from '@components/AdvComment/AdvCommentEditor'
import { runInAction } from 'mobx'

interface AdvCommentListProps {
  commentType: USER_COMMENT_TYPE
  fromViewType: FROM_VIEW_TYPE
  targetId: string // 회사픽 [고정배, 프리미엄 분석가] 픽 혹은 나도 승부사 분석글 아이디
  callbackDeleted?: (comment: Schema$UserComment) => void
  callbackCreated?: (comment: Schema$UserComment) => void
  callbackUpdated?: (comment: Schema$UserComment) => void
  callbackLiked?: (comment: Schema$UserComment) => void
}

export const AdvCommentList: React.FC<AdvCommentListProps> = observer((props: AdvCommentListProps) => {
  const {
    commentType, targetId, fromViewType,
    callbackCreated, callbackUpdated, callbackDeleted, callbackLiked,
  } = props
  const { commentStore } = useRootStore()

  const {
    commentMap, commentTopEditorFocus,
  } = commentStore

  const { comments, hasMore } = commentMap[commentType]

  const dataSource = new ListView.DataSource({
    rowHasChanged: (row1, row2) => row1 !== row2,
  })

  useOnMount(() => {
    fetch(true).catch(err => console.error(err))
  })

  useOnUnmount(() => {
    commentStore.resetComments(commentType)
  })

  const fetch = async (reset = false): Promise<void> => {
    try {
      await commentStore.fetchComments(commentType, targetId, reset)
    } catch (err) {
      console.error(err)
    }
  }

  const onCreateComment = (createdComment: Schema$UserComment): void => {
    const commentType = createdComment.commentType
    commentStore.createCommentHandler(createdComment)
    commentStore.setCommentTopEditorFocus(false, commentType)
    if (callbackCreated) {
      callbackCreated(createdComment)
    }
  }

  const onDeleteComment = (deletedComment: Schema$UserComment): void => {
    commentStore.deleteCommentHandler(deletedComment)
    if (callbackDeleted) {
      callbackDeleted(deletedComment)
    }
  }

  const onUpdateComment = (updatedComment: Schema$UserComment): void => {
    commentStore.updateCommentHandler(updatedComment)
    if (callbackUpdated) {
      callbackUpdated(updatedComment)
    }
  }

  const onToggleCommentLike = (updatedComment: Schema$UserComment): void => {
    commentStore.toggleCommentLikeHandler(updatedComment)
    if (callbackLiked) {
      callbackLiked(updatedComment)
    }
  }

  const onClickToggleVisibleEditor = (item: Schema$UserCommentExt): void => {
    const commentType = item.commentType
    const isOpen = commentStore.toggleVisibleEditorHandler(item)
    if (isOpen && commentStore.commentTopEditorFocus) {
      commentStore.setCommentTopEditorFocus(false, commentType)
    }
  }

  const onCloseEditor = () => {
    commentStore.setCommentTopEditorFocus(false, commentType)
  }

  const onOpenEditor = () => {
    commentStore.setCommentTopEditorFocus(true, commentType)
  }

  const onUserBlockSuccessHandler = (
    userPromiseId: string,
    blockTypes: COMMUNICATION_BLOCK_TYPE[]
  ) => {
    runInAction(() => {
      const isUserBlock = !_.isEmpty(blockTypes)
      const isCommentBlock = _.includes(blockTypes, COMMUNICATION_BLOCK_TYPE.PICK_COMMENT)

      commentStore.commentMap[commentType].comments = commentStore.setCommentBlock(
        comments,
        userPromiseId,
        isUserBlock,
        isCommentBlock
      )
    })
  }

  const renderHeader = useCallback((): React.ReactElement => {
    return (
      <AdvCommentEditor
        fromViewType={fromViewType}
        commentProp={{ id: null, targetId, commentType }}
        isFocus={commentTopEditorFocus}
        showBettingHistory
        callbackCreated={onCreateComment}
        callbackUpdated={onUpdateComment}
        callbackClose={onCloseEditor}
        callbackOpen={onOpenEditor}
      />
    )
  }, [commentTopEditorFocus])

  const renderRow = useCallback((row: Schema$UserCommentExt): React.ReactElement => {
    const item = (
      <AdvCommentListItem
        key={`user-comment-row-item-${row.id}`}
        userComment={row}
        callbackCreated={onCreateComment}
        callbackUpdated={onUpdateComment}
        callbackDeleted={onDeleteComment}
        callbackLiked={onToggleCommentLike}
        fromViewType={fromViewType}
        toggleEditor={onClickToggleVisibleEditor}
        onUserBlockSuccessHandler={onUserBlockSuccessHandler}
      />
    )
    if (_.isEmpty(row.replies)) {
      return item
    }
    const replyComponents: React.ReactNode[] = []
    if (!_.isEmpty(row.replies)) {
      for (const replyComment of row.replies) {
        replyComponents.push(
          <AdvCommentListItem
            key={`user-comment-reply-item-${replyComment.id}`}
            userComment={replyComment as Schema$UserCommentExt}
            callbackCreated={onCreateComment}
            callbackUpdated={onUpdateComment}
            callbackDeleted={onDeleteComment}
            callbackLiked={onToggleCommentLike}
            fromViewType={fromViewType}
            toggleEditor={onClickToggleVisibleEditor}
            onUserBlockSuccessHandler={onUserBlockSuccessHandler}
          />,
        )
      }
    }

    return (
      <Fragment>
        {item}
        <div className={'reply-comments'}>{replyComponents}</div>
      </Fragment>
    )
  }, [comments])

  const renderFooter = useCallback(() => {
    let footer = <Fragment/>
    if (fromViewType === FROM_VIEW_TYPE.USER_PICK_COMMENT || fromViewType === FROM_VIEW_TYPE.COMPANY_PICK_COMMENT) {
      if (_.size(comments) <= 0) {
        footer = <AdvEmpty ico={EMPTY_ICON.PICK_COMMENT_LIST}/>
      }
    }

    return <div css={{ textAlign: 'center' }}>{footer}</div>
  }, [comments])

  const endReachedHandler = async () => {
    if (hasMore) {
      await fetch()
    }
  }

  return (
    <AdvComments className={isMobile ? 'pick-comment-popup' : 'pick-comment-popup pc'}>
      <ListView
        css={{ height: 'calc(100% - 40px)' }}
        dataSource={dataSource.cloneWithRows(comments)}
        renderHeader={renderHeader}
        renderRow={renderRow}
        scrollRenderAheadDistance={500}
        renderFooter={renderFooter}
        onEndReached={endReachedHandler}
        onEndReachedThreshold={10}
        initialListSize={12}
        pageSize={20}
        className={isMobile ? 'pick-comments-list popup' : 'pick-comments-list popup pc-list'}
      />
    </AdvComments>
  )
})
